import { formatIDR } from "../../../utils/helpers/formatIDR";
import ItemAvatar from "../../../components/ItemAvatar";
import "../../PurchasePage/FormPurchase/style.css";
import "./style.css";

const PAYMENT_METHOD_LABELS = {
  cash: "Tunai",
  transfer: "Transfer",
  qris: "QRIS",
};

const STATUS_META = {
  draft: { label: "Draft", className: "bg-warning-lt" },
  paid: { label: "Lunas", className: "bg-primary-lt" },
  unpaid: { label: "Belum lunas", className: "bg-warning-lt" },
  cancel: { label: "Dibatalkan", className: "bg-danger-lt" },
  cancelled: { label: "Dibatalkan", className: "bg-danger-lt" },
};

const formatQuantity = (value) =>
  Number(value || 0).toLocaleString("id-ID", {
    maximumFractionDigits: 3,
  });

const formatDate = (value, pattern = "dd MMMM yyyy") => {
  if (!value) return "-";

  const date = /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? new Date(...value.split("-").map((part, index) => Number(part) - (index === 1 ? 1 : 0)))
    : new Date(value);

  if (Number.isNaN(date.getTime())) return "-";

  const includeTime = pattern.includes("HH:mm");
  return new Intl.DateTimeFormat("id-ID", {
    day: "2-digit",
    month: pattern.includes("MMMM") ? "long" : "short",
    year: "numeric",
    ...(includeTime ? { hour: "2-digit", minute: "2-digit", hourCycle: "h23" } : {}),
  }).format(date);
};

const DetailSale = ({ sale, loading = false, onPrint }) => {
  const details = sale?.details || [];
  const subtotal = Number(sale?.subtotal ?? details.reduce((sum, item) => sum + Number(item?.sub_total || 0), 0));
  const totalQuantity = Number(sale?.total_qty ?? details.reduce((sum, item) => sum + Number(item?.qty || 0), 0));
  const status = STATUS_META[sale?.payment_status] || {
    label: sale?.payment_status || "-",
    className: "bg-secondary-lt",
  };
  const invoiceNumber = sale?.invoice_no || `#${sale?.id || "-"}`;

  return (
    <div
      className="modal modal-blur fade sale-detail-modal"
      id="modal-sale-detail"
      tabIndex="-1"
      role="dialog"
      aria-modal="true"
      aria-labelledby="sale-detail-title"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
      style={{ backgroundColor: "rgba(0, 0, 0, 0.3)" }}>
      <div className="modal-dialog modal-dialog-centered modal-xl purchase-modal-dialog" role="document">
        <div className="modal-content purchase-modal-content">
          <div className="modal-header purchase-modal-header">
            <div className="d-flex align-items-center gap-3">
              <span className="purchase-modal-icon">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M6 3h12v18l-3-2l-3 2l-3-2l-3 2z" />
                  <path d="M9 8h6M9 12h6M9 16h3" />
                </svg>
              </span>
              <div>
                <div className="text-secondary small fw-semibold text-uppercase">Hanya lihat</div>
                <h5 className="modal-title mb-0" id="sale-detail-title">
                  Detail Penjualan {invoiceNumber}
                </h5>
              </div>
            </div>
            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Tutup detail penjualan" />
          </div>

          <div className="modal-body purchase-modal-body">
            {loading ? (
              <div className="sale-detail-loading" role="status" aria-live="polite">
                <span className="spinner-border spinner-border-sm text-primary" aria-hidden="true" />
                <div>
                  <strong>Memuat detail penjualan</strong>
                  <span>Menyiapkan informasi transaksi dan produk...</span>
                </div>
              </div>
            ) : (
              <>
                <section className="purchase-detail-hero">
                  <div>
                    <span>Total penjualan</span>
                    <strong>{formatIDR(sale?.total || 0)}</strong>
                    <small>
                      {invoiceNumber} · {formatDate(sale?.sale_date)}
                    </small>
                  </div>
                  <div className="purchase-detail-payment-grid">
                    <div>
                      <span>Dibayar</span>
                      <strong>{formatIDR(sale?.pay || 0)}</strong>
                    </div>
                    <div>
                      <span>Sisa piutang</span>
                      <strong>{formatIDR(sale?.due || 0)}</strong>
                    </div>
                    <span className={`badge ${status.className}`}>{status.label}</span>
                  </div>
                </section>

                <div className="row g-4 mt-0">
                  <div className="col-lg-4">
                    <section className="purchase-section h-100">
                      <div className="purchase-section-heading compact">
                        <div>
                          <div className="purchase-step">01</div>
                          <div>
                            <h3>Informasi transaksi</h3>
                            <p>Identitas penjualan dan pembayaran.</p>
                          </div>
                        </div>
                      </div>

                      <dl className="purchase-detail-list">
                        <div>
                          <dt>Nomor invoice</dt>
                          <dd>{invoiceNumber}</dd>
                        </div>
                        <div>
                          <dt>Pelanggan</dt>
                          <dd>{sale?.customer_name || "Pelanggan umum"}</dd>
                          {sale?.customer_phone ? <small>{sale.customer_phone}</small> : null}
                        </div>
                        <div>
                          <dt>Kasir</dt>
                          <dd>{sale?.cashier_name || "-"}</dd>
                        </div>
                        <div>
                          <dt>Metode pembayaran</dt>
                          <dd>{PAYMENT_METHOD_LABELS[sale?.payment_method] || sale?.payment_method || "-"}</dd>
                        </div>
                        <div>
                          <dt>Tanggal penjualan</dt>
                          <dd>{formatDate(sale?.sale_date)}</dd>
                        </div>
                        <div>
                          <dt>Dibuat</dt>
                          <dd>{formatDate(sale?.created_at, "dd MMM yyyy, HH:mm")}</dd>
                        </div>
                        {sale?.updated_at ? (
                          <div>
                            <dt>Terakhir diubah</dt>
                            <dd>{formatDate(sale.updated_at, "dd MMM yyyy, HH:mm")}</dd>
                          </div>
                        ) : null}
                      </dl>

                      <div className="sale-detail-calculation" aria-label="Ringkasan perhitungan penjualan">
                        <div>
                          <span>Subtotal produk</span>
                          <strong>{formatIDR(subtotal)}</strong>
                        </div>
                        <div>
                          <span>Diskon</span>
                          <strong>- {formatIDR(sale?.discount || 0)}</strong>
                        </div>
                        <div>
                          <span>Pajak</span>
                          <strong>+ {formatIDR(sale?.tax || 0)}</strong>
                        </div>
                        <div className="sale-detail-calculation-total">
                          <span>Total akhir</span>
                          <strong>{formatIDR(sale?.total || 0)}</strong>
                        </div>
                        {Number(sale?.change || 0) > 0 ? (
                          <div>
                            <span>Kembalian</span>
                            <strong>{formatIDR(sale.change)}</strong>
                          </div>
                        ) : null}
                      </div>

                      {sale?.bills?.length > 0 ? (
                        <div className="sale-detail-bills">
                          <div className="sale-detail-bills-title">
                            <span>Rincian tagihan</span>
                            <strong>{sale.bills.length} bill</strong>
                          </div>
                          {sale.bills.map((bill) => (
                            <div className="sale-detail-bill-row" key={bill.id}>
                              <div>
                                <strong>{bill.label || bill.bill_no}</strong>
                                <small>{bill.bill_no}</small>
                              </div>
                              <div>
                                <strong>{formatIDR(bill.total || 0)}</strong>
                                <small>{bill.payment_status === "paid" ? "Lunas" : `Sisa ${formatIDR(bill.due || 0)}`}</small>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : null}

                      {sale?.note ? (
                        <div className="purchase-detail-note">
                          <span>Catatan</span>
                          <p>{sale.note}</p>
                        </div>
                      ) : null}
                      {sale?.cancellation_reason ? (
                        <div className="purchase-detail-note border-danger bg-danger-lt">
                          <span className="text-danger">Alasan pembatalan</span>
                          <p>{sale.cancellation_reason}</p>
                        </div>
                      ) : null}
                    </section>
                  </div>

                  <div className="col-lg-8">
                    <section className="purchase-section h-100">
                      <div className="purchase-section-heading compact">
                        <div>
                          <div className="purchase-step">02</div>
                          <div>
                            <h3>Produk terjual</h3>
                            <p>Jumlah, satuan, dan harga merupakan snapshot saat transaksi dibuat.</p>
                          </div>
                        </div>
                      </div>

                      <div className="purchase-detail-items">
                        {details.length > 0 ? (
                          details.map((item, index) => (
                            <article
                              className="purchase-detail-item sale-detail-item"
                              key={item?.id || `${item?.product_id}-${index}`}>
                              <div className="purchase-detail-item-main">
                                <ItemAvatar
                                  name={item?.product_name || "Produk"}
                                  image={item?.image_path || item?.image}
                                  size="md"
                                />
                                <div>
                                  <strong>{item?.product_name || "Produk tidak tersedia"}</strong>
                                  <span>
                                    #{String(index + 1).padStart(2, "0")} · Produk #{item?.product_id || "-"}
                                  </span>
                                  {item?.note ? <small className="sale-detail-item-note">Catatan: {item.note}</small> : null}
                                </div>
                              </div>
                              <div>
                                <span>Jumlah jual</span>
                                <strong>
                                  {formatQuantity(item?.qty)} {item?.unit || "pcs"}
                                </strong>
                              </div>
                              <div>
                                <span>Harga satuan</span>
                                <strong>{formatIDR(item?.unit_price || 0)}</strong>
                              </div>
                              <div className="purchase-detail-item-total">
                                <span>Subtotal</span>
                                <strong>{formatIDR(item?.sub_total || 0)}</strong>
                              </div>
                            </article>
                          ))
                        ) : (
                          <div className="purchase-empty-state">
                            <strong>Tidak ada rincian produk</strong>
                            <span>Transaksi ini belum memiliki item penjualan.</span>
                          </div>
                        )}
                      </div>
                    </section>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="modal-footer purchase-modal-footer">
            <div className="purchase-footer-summary">
              <span>
                {details.length} jenis produk · {formatQuantity(totalQuantity)} item
              </span>
              <strong>{formatIDR(sale?.total || 0)}</strong>
            </div>
            <div className="d-flex gap-2 ms-auto">
              <button type="button" className="btn btn-ghost-secondary" data-bs-dismiss="modal">
                Tutup
              </button>
              <button
                type="button"
                className="btn btn-primary"
                data-bs-dismiss="modal"
                aria-label="Cetak Invoice"
                disabled={!sale?.id || loading}
                onClick={() => onPrint?.(sale?.id)}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="icon icon-tabler icons-tabler-outline icon-tabler-printer">
                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                  <path d="M17 17h2a2 2 0 0 0 2 -2v-4a2 2 0 0 0 -2 -2h-14a2 2 0 0 0 -2 2v4a2 2 0 0 0 2 2h2" />
                  <path d="M17 9v-4a2 2 0 0 0 -2 -2h-6a2 2 0 0 0 -2 2v4" />
                  <path d="M7 15a2 2 0 0 1 2 -2h6a2 2 0 0 1 2 2v4a2 2 0 0 1 -2 2h-6a2 2 0 0 1 -2 -2l0 -4" />
                </svg>
                Cetak Struk
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailSale;
